/**
 * ChatWidget.js — Floating support chat widget
 * Injected into Layout.js so it appears on every page.
 * Uses 3-second polling when chat is open.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, Send, Minimize2, LifeBuoy, Loader2, Star, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import apiService from '../../services/api';

const POLL_INTERVAL = 5000; // 5 seconds — balanced between real-time feel and server load

const BOT_CATEGORIES = [
  { value: 'search',          label: '🔍 Search Issues' },
  { value: 'trademark',       label: '™️ Trademark Status' },
  { value: 'legal_drafting',  label: '📄 Legal Drafting' },
  { value: 'brand_generator', label: '✨ Brand Generator' },
  { value: 'billing',         label: '💳 Billing & License' },
  { value: 'technical',       label: '🔧 Technical Issue' },
  { value: 'other',           label: '💬 Something Else' },
];

// ─── Message Bubble ───────────────────────────────────────────────────────────
const Bubble = ({ msg, onSelectCategory }) => {
  const isBot    = msg.sender_type === 'bot';
  const isSystem = msg.sender_type === 'bot' && msg.msg_type === 'system';
  const isAgent  = msg.sender_type === 'agent';
  const isUser   = !isBot && !isAgent;
  const isOptions = msg.msg_type === 'bot_options';

  const time = new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  if (isSystem) return (
    <div className="text-center my-2">
      <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">{msg.message}</span>
    </div>
  );

  if (isOptions) {
    const opts = msg.options || [];
    return (
      <div className="my-2 space-y-1.5">
        {opts.map(opt => (
          <button
            key={opt.value}
            onClick={() => onSelectCategory && onSelectCategory(opt)}
            className="w-full text-left px-3 py-2 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 text-sm text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors font-medium"
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex gap-2 my-1 ${isUser ? 'flex-row-reverse' : ''}`}>
      {(isBot || isAgent) && (
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-1 ${
          isAgent ? 'bg-emerald-500 text-white' : 'bg-violet-600 text-white'
        }`}>
          {isAgent ? 'LL' : '🤖'}
        </div>
      )}
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-violet-600 text-white rounded-tr-sm'
            : isAgent
            ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 rounded-tl-sm'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-tl-sm'
        }`}>
          <p className="whitespace-pre-wrap">{msg.message}</p>
        </div>
        <span className="text-[9px] text-gray-400 mt-0.5 px-1">{time}</span>
      </div>
    </div>
  );
};

// ─── Rating Stars ─────────────────────────────────────────────────────────────
const RatingStars = ({ onRate }) => {
  const [hover, setHover]   = useState(0);
  const [rating, setRating] = useState(0);

  const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  return (
    <div className="p-4 text-center space-y-3">
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">How was your support experience?</p>
      <div className="flex justify-center gap-2">
        {[1,2,3,4,5].map(n => (
          <button
            key={n}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => { setRating(n); onRate(n); }}
            className="text-2xl transition-transform hover:scale-125"
          >
            <Star className={`w-7 h-7 ${(hover || rating) >= n ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
          </button>
        ))}
      </div>
      {(hover || rating) > 0 && (
        <p className="text-xs font-medium text-violet-600">{labels[hover || rating]}</p>
      )}
      <button onClick={() => onRate(0)} className="text-xs text-gray-400 hover:text-gray-600">Skip</button>
    </div>
  );
};

// ─── Main Widget ──────────────────────────────────────────────────────────────
const ChatWidget = () => {
  const [open,        setOpen]        = useState(false);
  const [minimized,   setMinimized]   = useState(false);
  const [messages,    setMessages]    = useState([]);
  const [sessionId,   setSessionId]   = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [input,       setInput]       = useState('');
  const [sending,     setSending]     = useState(false);
  const [starting,    setStarting]    = useState(false);
  const [ended,       setEnded]       = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [unread,      setUnread]      = useState(0);
  const [categorySelected, setCategorySelected] = useState(false);
  const [agentJoined, setAgentJoined] = useState(false);

  const lastIdRef     = useRef(0);
  const pollRef       = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef      = useRef(null);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Check agent status — only when needed
  // Open + no session: every 5s (waiting for agent to come online)
  // Open + active session: every 60s (just background refresh)
  // Closed: every 60s (just for the floating button badge)
  useEffect(() => {
    const checkStatus = () => {
      apiService.get('/support/chat/status')
        .then(res => { if (res?.success) setAgentStatus(res.data); })
        .catch(() => {});
    };
    checkStatus();
    const interval = (!open) ? 60000          // closed — very infrequent
                   : sessionId ? 60000        // active session — background only
                   : 5000;                    // open, waiting — check frequently
    const statusInterval = setInterval(checkStatus, interval);
    return () => clearInterval(statusInterval);
  }, [open, !!sessionId]);

  // Auto-start chat when agent comes online and widget is open with no session
  useEffect(() => {
    if (open && !sessionId && !ended && !starting && agentStatus?.available === true) {
      startChat();
    }
  }, [agentStatus?.available]); // triggers when availability changes

  // Polling — fetch new messages every 3s when chat is open and active
  const poll = useCallback(async () => {
    if (!sessionId || ended) return;
    try {
      const res = await apiService.get(`/support/chat/messages/${sessionId}`, { last_id: lastIdRef.current });
      if (res?.success && res.data?.length > 0) {
        const newMsgs = res.data;
        lastIdRef.current = newMsgs[newMsgs.length - 1].id;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const fresh = newMsgs.filter(m => !existingIds.has(m.id));
          return [...prev, ...fresh];
        });
        // Track if agent joined
        const hasAgentMsg = newMsgs.some(m => m.sender_type === 'agent');
        if (hasAgentMsg) setAgentJoined(true);
        // Unread count when minimized
        if (minimized || !open) {
          const userMsgs = newMsgs.filter(m => m.sender_type !== 'user' && m.sender_type !== 'client' && m.sender_type !== 'client_child_user');
          setUnread(u => u + userMsgs.length);
        }
      }
      if (res?.session) {
        setSessionStatus(res.session.status);
        if (res.session.status === 'active' && res.session.agent_id) setAgentJoined(true);
      }
    } catch { /* silent */ }
  }, [sessionId, ended, minimized, open]);

  useEffect(() => {
    if (sessionId && !ended && open) {
      // Poll faster when widget is open and visible, slower when minimized
      const interval = minimized ? 15000 : POLL_INTERVAL;
      pollRef.current = setInterval(poll, interval);
      return () => clearInterval(pollRef.current);
    }
    // Stop polling when widget is closed — resume when reopened
    if (!open) clearInterval(pollRef.current);
  }, [sessionId, ended, open, minimized, poll]);

  // Start chat
  const startChat = async () => {
    setStarting(true);
    // Reset all state before starting
    setMessages([]);
    setSessionId(null);
    setEnded(false);
    setSessionStatus(null);
    setCategorySelected(false);
    setAgentJoined(false);
    lastIdRef.current = 0;
    clearInterval(pollRef.current);
    try {
      const res = await apiService.post('/support/chat/start', {});
      if (res?.success) {
        const sid = res.data.session_id;
        setSessionId(sid);
        // Load existing messages (bot greeting etc)
        const msgRes = await apiService.get(`/support/chat/messages/${sid}`, { last_id: 0 });
        if (msgRes?.success && msgRes.data && msgRes.data.length > 0) {
          setMessages(msgRes.data);
          lastIdRef.current = msgRes.data[msgRes.data.length - 1].id;
        }
      }
    } catch { /* silent */ }
    finally { setStarting(false); }
  };

  // Open widget
  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    setUnread(0);
    // Only start chat if agents are available AND no active session
    const agentsAvailable = agentStatus?.available !== false;
    if (!sessionId && agentsAvailable) startChat();
  };

  // Send message
  const handleSend = async () => {
    if (!input.trim() || !sessionId || sending) return;
    setSending(true);
    const text = input.trim();
    setInput('');
    try {
      await apiService.post(`/support/chat/send/${sessionId}`, { message: text });
      // Immediately poll for new messages so reply appears fast without waiting 3s
      await poll();
    } catch { /* silent */ }
    finally { setSending(false); inputRef.current?.focus(); }
  };

  // Select category from bot options
  const handleSelectCategory = async (opt) => {
    if (!sessionId) return;
    setCategorySelected(true);
    // Remove options message from UI
    setMessages(prev => prev.filter(m => m.msg_type !== 'bot_options'));
    try {
      const res = await apiService.post(`/support/chat/send/${sessionId}`, { category: opt.value });
      if (res?.success) {
        // Poll immediately to get both user message + bot response from DB
        if (res.data && res.data.id > lastIdRef.current) lastIdRef.current = res.data.id;
        await poll();
      }
    } catch { /* silent */ }
  };

  // End chat
  const handleEnd = async (rating = 0) => {
    if (sessionId) {
      await apiService.put(`/support/chat/end/${sessionId}`, { rating }).catch(() => {});
    }
    setEnded(true);
    setSessionId(null);
    clearInterval(pollRef.current);
  };

  const isClosed = sessionStatus === 'closed' || ended;
  const isOffline  = agentStatus?.available === false && !sessionId;
  const showInput  = sessionId && !isClosed && categorySelected;

  // ── Floating button ──────────────────────────────────────────────────────────
  const FloatButton = (
    <button
      onClick={handleOpen}
      className="fixed bottom-6 right-6 z-[9998] w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 text-white shadow-2xl shadow-violet-500/40 hover:shadow-violet-500/60 hover:scale-110 active:scale-95 transition-all duration-200 flex items-center justify-center"
    >
      <MessageSquare className="w-6 h-6" />
      {unread > 0 && (
        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );

  // ── Chat window ──────────────────────────────────────────────────────────────
  const ChatWindow = (
    <div className={`fixed bottom-24 right-6 z-[9999] w-[360px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ${
      minimized ? 'h-14' : 'h-[520px]'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-700 rounded-t-2xl flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
            <LifeBuoy className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">LegalLumo Support</p>
            <p className="text-[10px] text-violet-200 flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${
                (agentJoined || sessionStatus === 'active') ? 'bg-emerald-300'
                : agentStatus?.available === false ? 'bg-red-400'
                : 'bg-amber-300'
              }`} />
              {(agentJoined || sessionStatus === 'active') ? 'Agent connected'
               : agentStatus?.available === false ? 'No agents online right now'
               : 'Connecting you to an agent…'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(v => !v)} className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-colors">
            {minimized ? <ChevronDown className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 min-h-0">
            {!sessionId && agentStatus?.available === false ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <span className="text-2xl">😴</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800 dark:text-gray-200">No agents online</p>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    Our support team is currently offline.<br />
                    Please raise a <a href="/support" className="text-violet-600 hover:underline font-medium">support ticket</a> and we'll get back to you.
                  </p>
                </div>
                <div className="text-[10px] text-gray-400 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-xl">
                  Mon–Sat · 9 AM – 7 PM IST
                </div>
              </div>
            ) : starting ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">Starting chat…</div>
            ) : (
              messages.map((msg, i) => (
                <Bubble
                  key={msg.id || i}
                  msg={msg}
                  onSelectCategory={!categorySelected ? handleSelectCategory : null}
                />
              ))
            )}

            {/* Ended — show rating */}
            {isClosed && !ended && (
              <RatingStars onRate={handleEnd} />
            )}
            {ended && (
              <div className="text-center py-6 space-y-3">
                <p className="text-sm font-semibold text-emerald-600">✅ Chat ended. Thank you!</p>
                <button
                  onClick={() => startChat()}
                  className="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors"
                >
                  Start New Chat
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {showInput && (
            <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800 flex gap-2 flex-shrink-0">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Type a message…"
                className="flex-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 transition"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="w-9 h-9 rounded-xl bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center transition-colors disabled:opacity-40"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}

          {!showInput && !isClosed && !ended && categorySelected && (
            <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-800 text-center flex-shrink-0">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Waiting for an agent to join…
              </p>
            </div>
          )}

          {/* End chat button */}
          {sessionId && !isClosed && (
            <div className="px-3 pb-2 text-center flex-shrink-0">
              <button
                onClick={() => handleEnd(0)}
                className="text-[10px] text-gray-400 hover:text-red-500 transition-colors"
              >
                End chat
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return createPortal(
    <>
      {!open && FloatButton}
      {open  && FloatButton}
      {open  && ChatWindow}
    </>,
    document.body
  );
};

export default ChatWidget;